const SESSION_STORAGE_KEY = "chatAiWebSessionId";
const MESSAGES_STORAGE_KEY = "chatAiWebMessages";
const FINISHED_STORAGE_KEY = "chatAiWebFinished";

export function getStoredSessionId() {
  try {
    return sessionStorage.getItem(SESSION_STORAGE_KEY) || "";
  } catch {
    return "";
  }
}

export function setStoredSessionId(id) {
  try {
    if (id) {
      sessionStorage.setItem(SESSION_STORAGE_KEY, id);
      sessionStorage.removeItem(FINISHED_STORAGE_KEY);
    } else {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
      sessionStorage.removeItem(MESSAGES_STORAGE_KEY);
    }
  } catch {
    /* ignore */
  }
}

export function getStoredMessages() {
  try {
    const raw = sessionStorage.getItem(MESSAGES_STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter(
      (m) =>
        m &&
        typeof m.id === "string" &&
        (m.role === "assistant" || m.role === "user") &&
        typeof m.text === "string" &&
        Array.isArray(m.links)
    );
  } catch {
    return [];
  }
}

export function setStoredMessages(messages) {
  try {
    if (!Array.isArray(messages) || messages.length === 0) {
      sessionStorage.removeItem(MESSAGES_STORAGE_KEY);
      return;
    }
    sessionStorage.setItem(MESSAGES_STORAGE_KEY, JSON.stringify(messages));
  } catch {
    /* ignore */
  }
}

export function isChatMarkedFinished() {
  try {
    return sessionStorage.getItem(FINISHED_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

export function setChatFinished(finished) {
  try {
    if (finished) {
      sessionStorage.setItem(FINISHED_STORAGE_KEY, "1");
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
      sessionStorage.removeItem(MESSAGES_STORAGE_KEY);
    } else {
      sessionStorage.removeItem(FINISHED_STORAGE_KEY);
    }
  } catch {
    /* ignore */
  }
}

/**
 * POST /web-chats — see chat-ai WebChatHandler.
 * @param {{ message: string; sessionId?: string }} body
 * @returns {Promise<{ reply: string; sessionId: string; links: string[] }>}
 */
export async function postWebChat(body) {
  const url = process.env.REACT_APP_CHAT_AI_API_URL;
  const token = process.env.REACT_APP_CHAT_AI_API_TOKEN;
  if (!url || !token) {
    throw new Error(
      "Chat is not configured. Set REACT_APP_CHAT_AI_API_URL and REACT_APP_CHAT_AI_API_TOKEN."
    );
  }

  const payload = { message: body.message };
  if (body.sessionId) {
    payload.sessionId = body.sessionId;
  }

  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-api-token": token,
    },
    body: JSON.stringify(payload),
  });

  let data = {};
  try {
    data = await res.json();
  } catch {
    data = {};
  }

  if (!res.ok) {
    const msg =
      typeof data.message === "string" && data.message
        ? data.message
        : `Request failed (${res.status})`;
    throw new Error(msg);
  }

  if (typeof data.reply !== "string" || !data.sessionId) {
    throw new Error("Unexpected response from chat service.");
  }

  const links = Array.isArray(data.links)
    ? data.links.filter((v) => typeof v === "string")
    : [];

  return { reply: data.reply, sessionId: data.sessionId, links };
}
